import React, { useState, useMemo, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { X, Search, Upload, Check } from "lucide-react";
import { Button } from "../../components/Button";
import { FontPickerModalProps, FontPickerTab, SelectedFont } from "./types";
import { systemFonts, SystemFont } from "./data/systemFonts";
import {
  googleFonts,
  GoogleFont,
  preloadAllGoogleFonts,
} from "./data/googleFonts";
import { getFontPickerStyles } from "./fontPickerStyles";
import { getPortalContainer } from "./utils";

export const FontPickerModal: React.FC<FontPickerModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  isLoading = false,
}) => {
  const [activeTab, setActiveTab] = useState<FontPickerTab>("system");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFont, setSelectedFont] = useState<SelectedFont | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [customFontName, setCustomFontName] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Track if Google Fonts have been preloaded
  const [googleFontsPreloaded, setGoogleFontsPreloaded] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchQuery("");
      setSelectedFont(null);
      setUploadedFile(null);
      setCustomFontName("");
    }
  }, [isOpen]);

  // Preload all Google Fonts when Google Fonts tab is first selected
  useEffect(() => {
    if (activeTab === "google" && !googleFontsPreloaded) {
      preloadAllGoogleFonts().then(() => {
        setGoogleFontsPreloaded(true);
      });
    }
  }, [activeTab, googleFontsPreloaded]);

  // Filter system fonts based on search
  const filteredSystemFonts = useMemo(() => {
    if (!searchQuery) return systemFonts;
    const query = searchQuery.toLowerCase();
    return systemFonts.filter(
      (font) =>
        font.name.toLowerCase().includes(query) ||
        font.category.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  // Filter Google fonts based on search
  const filteredGoogleFonts = useMemo(() => {
    if (!searchQuery) return googleFonts;
    const query = searchQuery.toLowerCase();
    return googleFonts.filter(
      (font) =>
        font.name.toLowerCase().includes(query) ||
        font.category.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  const handleSystemFontSelect = (font: SystemFont) => {
    setSelectedFont({
      type: "system",
      name: font.name,
      value: font.value,
    });
  };

  const handleGoogleFontSelect = (font: GoogleFont) => {
    // All available weights will be loaded when the font is added to the theme
    // Weight selection happens at usage time in widgets, not here
    setSelectedFont({
      type: "google",
      name: font.name,
    });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedFile(file);
      // Use filename without extension as default name
      const nameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
      setCustomFontName(nameWithoutExt);
      setSelectedFont({
        type: "upload",
        name: nameWithoutExt,
        file,
        format: getFileFormat(file.name),
      });
    }
  };

  const getFileFormat = (filename: string): string => {
    const ext = filename.split(".").pop()?.toLowerCase();
    switch (ext) {
      case "woff2":
        return "woff2";
      case "woff":
        return "woff";
      case "ttf":
        return "truetype";
      case "otf":
        return "opentype";
      default:
        return "woff2";
    }
  };

  const handleCustomNameChange = (name: string) => {
    setCustomFontName(name);
    if (selectedFont?.type === "upload" && uploadedFile) {
      setSelectedFont({
        ...selectedFont,
        name,
      });
    }
  };

  const handleConfirm = () => {
    if (selectedFont) {
      onSelect(selectedFont);
      onClose();
    }
  };

  const isSelectDisabled =
    isLoading ||
    !selectedFont ||
    (selectedFont.type === "upload" &&
      (!uploadedFile || !customFontName.trim()));

  // Group Google fonts by category
  const googleFontsByCategory = useMemo(() => {
    const groups: Record<string, GoogleFont[]> = {};
    filteredGoogleFonts.forEach((font) => {
      if (!groups[font.category]) {
        groups[font.category] = [];
      }
      groups[font.category].push(font);
    });
    return groups;
  }, [filteredGoogleFonts]);

  // Group system fonts by category
  const systemFontsByCategory = useMemo(() => {
    const groups: Record<string, SystemFont[]> = {};
    filteredSystemFonts.forEach((font) => {
      if (!groups[font.category]) {
        groups[font.category] = [];
      }
      groups[font.category].push(font);
    });
    return groups;
  }, [filteredSystemFonts]);

  if (!isOpen) return null;

  const modalContent = (
    <>
      <style>{getFontPickerStyles()}</style>
      <div className="font-picker-overlay" onClick={onClose}>
        <div className="font-picker-modal" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="font-picker-header">
            <h2 className="font-picker-title">Select font</h2>
            <Button
              variant="icon"
              size="sm"
              onClick={onClose}
              aria-label="Close"
            >
              <X size={20} />
            </Button>
          </div>

          {/* Search */}
          <div className="font-picker-search">
            <Search size={16} className="font-picker-search-icon" />
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="font-picker-search-input"
              autoFocus
            />
          </div>

          {/* Tabs */}
          <div className="font-picker-tabs">
            <button
              className={`font-picker-tab ${activeTab === "system" ? "active" : ""}`}
              onClick={() => setActiveTab("system")}
            >
              System Fonts
            </button>
            <button
              className={`font-picker-tab ${activeTab === "google" ? "active" : ""}`}
              onClick={() => setActiveTab("google")}
            >
              Google Fonts
            </button>
            <button
              className={`font-picker-tab ${activeTab === "upload" ? "active" : ""}`}
              onClick={() => setActiveTab("upload")}
            >
              Upload
            </button>
          </div>

          {/* Content */}
          <div className="font-picker-content">
            {activeTab === "system" && (
              <div className="font-picker-list">
                <p className="font-picker-description">
                  These fonts load faster and might appear differently on
                  various devices.
                </p>
                {Object.entries(systemFontsByCategory).map(
                  ([category, fonts]) => (
                    <div key={category} className="font-picker-category">
                      <h4 className="font-picker-category-title">
                        {category.charAt(0).toUpperCase() + category.slice(1)}
                      </h4>
                      {fonts.map((font) => (
                        <div
                          key={font.name}
                          className={`font-picker-item ${
                            selectedFont?.type === "system" &&
                            selectedFont?.name === font.name
                              ? "selected"
                              : ""
                          }`}
                          onClick={() => handleSystemFontSelect(font)}
                        >
                          <span
                            className="font-picker-item-name"
                            style={{ fontFamily: font.value }}
                          >
                            {font.name}
                          </span>
                          {selectedFont?.type === "system" &&
                            selectedFont?.name === font.name && (
                              <Check size={16} className="font-picker-check" />
                            )}
                        </div>
                      ))}
                    </div>
                  )
                )}
              </div>
            )}

            {activeTab === "google" && (
              <div className="font-picker-list">
                <p className="font-picker-description">
                  These fonts are downloaded onto a visitor's computer and might
                  cause slower load times.
                </p>
                {Object.entries(googleFontsByCategory).map(
                  ([category, fonts]) => (
                    <div key={category} className="font-picker-category">
                      <h4 className="font-picker-category-title">
                        {category.charAt(0).toUpperCase() + category.slice(1)}
                      </h4>
                      {fonts.map((font) => {
                        const isSelected =
                          selectedFont?.type === "google" &&
                          selectedFont?.name === font.name;
                        return (
                          <div key={font.name}>
                            <div
                              className={`font-picker-item ${isSelected ? "selected" : ""}`}
                              onClick={() => handleGoogleFontSelect(font)}
                            >
                              <span
                                className="font-picker-item-name"
                                style={{
                                  fontFamily: googleFontsPreloaded
                                    ? `'${font.name}', sans-serif`
                                    : "inherit",
                                }}
                              >
                                {font.name}
                              </span>
                              {isSelected && (
                                <Check
                                  size={16}
                                  className="font-picker-check"
                                />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )
                )}
              </div>
            )}

            {activeTab === "upload" && (
              <div className="font-picker-upload">
                <p className="font-picker-description">
                  Upload your own font file. Supported formats: WOFF2, WOFF,
                  TTF, OTF.
                </p>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".woff2,.woff,.ttf,.otf"
                  onChange={handleFileSelect}
                  style={{ display: "none" }}
                />

                {!uploadedFile ? (
                  <div
                    className="font-picker-upload-zone"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload size={32} className="font-picker-upload-icon" />
                    <span className="font-picker-upload-text">
                      Click to upload font file
                    </span>
                    <span className="font-picker-upload-hint">
                      WOFF2, WOFF, TTF, or OTF
                    </span>
                  </div>
                ) : (
                  <div className="font-picker-upload-preview">
                    <div className="font-picker-upload-file">
                      <span className="font-picker-upload-filename">
                        {uploadedFile.name}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        Change
                      </Button>
                    </div>
                    <div className="font-picker-upload-name">
                      <label className="font-picker-upload-label">
                        Font name
                      </label>
                      <input
                        type="text"
                        value={customFontName}
                        onChange={(e) => handleCustomNameChange(e.target.value)}
                        placeholder="Enter font name"
                        className="font-picker-upload-input"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="font-picker-footer">
            <Button variant="outline" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={isSelectDisabled}
            >
              {isLoading ? "Adding..." : "Select"}
            </Button>
          </div>
        </div>
      </div>
    </>
  );

  // Portal to parent document when in iframe to escape mobile frame constraints
  return ReactDOM.createPortal(modalContent, getPortalContainer());
};
